// frontend/src/components/Footer.jsx
// FRONTEND_CODE_VERSION = "v1.1.0" // Crawlable /about, /contact; hash links as real hrefs
// Purpose: Site footer with links, legal info, and expandable dismissed sections

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext.jsx";

const Footer = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const currentLanguage = i18n.language;

  // Track dismissed sections
  const [expandedSection, setExpandedSection] = useState(null);
  const [howItWorksDismissed, setHowItWorksDismissed] = useState(() => {
    return localStorage.getItem("how_it_works_dismissed") === "true";
  });
  const [trustSignalsDismissed, setTrustSignalsDismissed] = useState(() => {
    return localStorage.getItem("trust_signals_dismissed") === "true";
  });

  // Listen for storage changes to update dismissed state
  useEffect(() => {
    const handleStorageChange = () => {
      setHowItWorksDismissed(
        localStorage.getItem("how_it_works_dismissed") === "true"
      );
      setTrustSignalsDismissed(
        localStorage.getItem("trust_signals_dismissed") === "true"
      );
    };

    window.addEventListener("storage", handleStorageChange);
    // Also check periodically (for same-tab updates)
    const interval = setInterval(handleStorageChange, 1000);
    // Check on mount
    handleStorageChange();

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  // Scroll to top helper function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <footer className="bg-gray-900 text-gray-300 mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">
              {t("footer.quick_links", "Quick Links")}
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  to="/"
                  className="hover:text-white transition-colors"
                  onClick={scrollToTop}
                >
                  {t("footer.home", "Home")}
                </Link>
              </li>
              {!isAuthenticated && (
                <li>
                  <Link
                    to={`/register?lang=${currentLanguage}`}
                    className="hover:text-white transition-colors"
                    onClick={scrollToTop}
                  >
                    {t("footer.sign_up", "Sign Up")}
                  </Link>
                </li>
              )}
              {isAuthenticated && (
                <li>
                  <Link
                    to={`/dashboard?lang=${currentLanguage}`}
                    className="hover:text-white transition-colors"
                    onClick={scrollToTop}
                  >
                    {t("footer.dashboard", "Dashboard")}
                  </Link>
                </li>
              )}
              <li>
                <Link
                  to="/about#how-it-works"
                  className="hover:text-white transition-colors"
                  onClick={scrollToTop}
                >
                  {t("footer.how_it_works", "How It Works")}
                </Link>
              </li>
              <li>
                <Link
                  to="/about#trust-signals"
                  className="hover:text-white transition-colors"
                  onClick={scrollToTop}
                >
                  {t("footer.why_choose", "Why Choose Alertique?")}
                </Link>
              </li>
              <li>
                <Link
                  to={`/about${currentLanguage ? `?lang=${currentLanguage}` : ""}`}
                  className="hover:text-white transition-colors"
                  onClick={scrollToTop}
                >
                  {t("footer.about", "About")}
                </Link>
              </li>
              <li>
                <Link
                  to={`/help?lang=${currentLanguage}`}
                  className="hover:text-white transition-colors"
                  onClick={scrollToTop}
                >
                  {t("footer.help", "Help & Support")}
                </Link>
              </li>
              <li>
                <Link
                  to={`/blog?lang=${currentLanguage}`}
                  className="hover:text-white transition-colors"
                  onClick={scrollToTop}
                >
                  {t("footer.blog", "Blog")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Dismissed Sections (Expandable) */}
          {(howItWorksDismissed || trustSignalsDismissed) && (
            <div>
              <h3 className="text-white font-semibold mb-4">
                {t("footer.learn_more", "Learn More")}
              </h3>
              <ul className="space-y-2 text-sm">
                {howItWorksDismissed && (
                  <li>
                    <button
                      onClick={() => toggleSection("how_it_works")}
                      className="flex items-center justify-between w-full text-left hover:text-white transition-colors"
                    >
                      <span>{t("footer.how_it_works", "How It Works")}</span>
                      <span className="text-xs">
                        {expandedSection === "how_it_works" ? "▼" : "▶"}
                      </span>
                    </button>
                    {expandedSection === "how_it_works" && (
                      <div className="mt-2 pl-4 text-xs text-gray-400">
                        <Link
                          to="/about#how-it-works"
                          className="text-blue-400 hover:text-blue-300 underline"
                          onClick={scrollToTop}
                        >
                          {t("footer.show_section", "Show on page")}
                        </Link>
                      </div>
                    )}
                  </li>
                )}
                {trustSignalsDismissed && (
                  <li>
                    <button
                      onClick={() => toggleSection("trust_signals")}
                      className="flex items-center justify-between w-full text-left hover:text-white transition-colors"
                    >
                      <span>{t("footer.why_choose", "Why Choose Alertique?")}</span>
                      <span className="text-xs">
                        {expandedSection === "trust_signals" ? "▼" : "▶"}
                      </span>
                    </button>
                    {expandedSection === "trust_signals" && (
                      <div className="mt-2 pl-4 text-xs text-gray-400">
                        <Link
                          to="/about#trust-signals"
                          className="text-blue-400 hover:text-blue-300 underline"
                          onClick={scrollToTop}
                        >
                          {t("footer.show_section", "Show on page")}
                        </Link>
                      </div>
                    )}
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Legal & Support */}
          <div>
            <h3 className="text-white font-semibold mb-4">
              {t("footer.legal", "Legal")}
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  to={`/privacy${currentLanguage ? `?lang=${currentLanguage}` : ""}`}
                  className="hover:text-white transition-colors"
                >
                  {t("footer.privacy", "Privacy Policy")}
                </Link>
              </li>
              <li>
                <Link
                  to={`/affiliate-disclosure${currentLanguage ? `?lang=${currentLanguage}` : ""}`}
                  className="hover:text-white transition-colors"
                >
                  {t("footer.affiliate", "Affiliate Disclosure")}
                </Link>
              </li>
              <li>
                <Link
                  to={`/terms${currentLanguage ? `?lang=${currentLanguage}` : ""}`}
                  className="hover:text-white transition-colors"
                >
                  {t("footer.terms", "Terms of Service")}
                </Link>
              </li>
              <li>
                <Link
                  to={`/accessibility${currentLanguage ? `?lang=${currentLanguage}` : ""}`}
                  className="hover:text-white transition-colors"
                >
                  {t("footer.accessibility", "Accessibility")}
                </Link>
              </li>
              <li>
                <Link
                  to={`/cookies${currentLanguage ? `?lang=${currentLanguage}` : ""}`}
                  className="hover:text-white transition-colors"
                >
                  {t("footer.cookies", "Cookies & Tracking")}
                </Link>
              </li>
              <li>
                <Link
                  to={`/contact${currentLanguage ? `?lang=${currentLanguage}` : ""}`}
                  className="hover:text-white transition-colors"
                >
                  {t("footer.contact", "Contact")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Features & Benefits */}
          <div>
            <h3 className="text-white font-semibold mb-4">
              {t("footer.features", "Features")}
            </h3>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <span>🔒</span>
                <span>{t("footer.secure", "100% Free & Secure")}</span>
              </li>
              <li className="flex items-center gap-2">
                <span>⚡</span>
                <span>{t("footer.real_time", "Real-time Tracking")}</span>
              </li>
              <li className="flex items-center gap-2">
                <span>🎁</span>
                <span>{t("footer.no_credit_card", "No Credit Card Required")}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-gray-800">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-sm text-gray-400">
              <p>
                © {new Date().getFullYear()} Alertique. {t("footer.all_rights", "All rights reserved.")}
              </p>
              <p className="mt-1">
                <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded">
                  {t("footer.beta", "Beta")}
                </span>
              </p>
            </div>
            <div className="flex items-center gap-4">
              {/* Language Switcher */}
              <button
                onClick={() => {
                  const newLang = currentLanguage === "en" ? "he" : "en";
                  i18n.changeLanguage(newLang);
                  scrollToTop();
                  navigate(`/?lang=${newLang}`);
                }}
                className="text-sm text-gray-400 hover:text-white transition-colors"
              >
                {currentLanguage === "en" ? "עברית" : "English"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

